import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { CalendarX2, CheckCircle2, AlertTriangle } from "lucide-react";

const WEEKDAYS = ["zo", "ma", "di", "wo", "do", "vr", "za"];

function formatDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${WEEKDAYS[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}`;
}

export default function MissingDaysDialog() {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalMissing, setTotalMissing] = useState(null);

  const fetchData = useCallback((m) => {
    setLoading(true);
    api.get("/journal/missing-days", { params: { month: m } })
      .then((r) => {
        setData(r.data);
        const tot = (r.data.locations || []).reduce((s, l) => s + l.missing_count, 0);
        setTotalMissing(tot);
      })
      .catch(() => toast.error("Kon ontbrekende dagen niet laden"))
      .finally(() => setLoading(false));
  }, []);

  // Preload current-month count for the badge on the trigger button
  useEffect(() => { fetchData(currentMonth); }, [fetchData, currentMonth]);
  useEffect(() => { if (open) fetchData(month); }, [open, month, fetchData]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-missing-days" className="relative">
          <CalendarX2 className="h-4 w-4 mr-2" /> Ontbrekende dagen
          {totalMissing > 0 && (
            <Badge className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="missing-days-badge">
              {totalMissing}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Ontbrekende dagen per vestiging</DialogTitle>
          <DialogDescription>
            Dagen in de gekozen maand waarvoor nog geen dagontvangst (of Z-ticket) is geboekt. Toekomstige dagen worden niet meegeteld.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="md-month">Maand</Label>
              <Input
                id="md-month"
                type="month"
                data-testid="missing-days-month"
                value={month}
                max={currentMonth}
                onChange={(e) => setMonth(e.target.value)}
                className="w-48"
              />
            </div>
            {data && (
              <div className="text-sm text-muted-foreground pb-2">
                {data.total_days} dag{data.total_days === 1 ? "" : "en"} in deze periode
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-10 text-center text-muted-foreground">Laden…</div>
          ) : (
            <div className="space-y-3">
              {(data?.locations || []).map((loc) => (
                <div key={loc.location_id} className="rounded-lg border border-border p-4" data-testid={`missing-loc-${loc.location_id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="font-medium">{loc.location_name}</div>
                    {loc.missing_count === 0 ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Volledig
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> {loc.missing_count} ontbreekt
                      </Badge>
                    )}
                  </div>
                  {loc.missing_count === 0 ? (
                    <p className="text-sm text-muted-foreground">Alle dagen zijn geboekt.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {loc.missing_days.map((d) => (
                        <span
                          key={d}
                          title={d}
                          data-testid={`missing-day-${d}`}
                          className="inline-flex items-center rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs font-mono text-amber-800"
                        >
                          {formatDay(d)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {data && data.locations.length === 0 && (
                <p className="text-center text-muted-foreground py-6">Geen actieve vestigingen.</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
